import type { ControlType } from '@risksmart-app/domain/src/types/consts';
import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import type {
  InsertChildControlInput,
  InsertChildControlMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type InsertControlTRPCInput = Omit<
  InsertChildControlInput,
  'schedule' | 'Type'
> &
  ScheduleTRPCInput & {
    Type?: ControlType | null;
  };

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
function mapTrpcResponseToGraphQL(trpcData: {
  Id: string;
}): InsertChildControlMutation {
  return {
    insertChildControl: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
}

export const useInsertControlTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.control.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlsByUserId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlsBasic.queryKey(),
        });
        // COI-471 parity: Apollo twin also evicts `risk`, `obligation` and
        // `linked_item` (a new control can be linked to risks/obligations and
        // affects their control coverage / risk scores).
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.obligation.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.linkedItem.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    insertControl: async (
      variables: InsertControlTRPCInput
    ): Promise<InsertChildControlMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
